import { useDialogContext, useStoreState } from "@ariakit/react";
import { LetterheadSubmitError, useForm } from "@indietabletop/appkit";
import type { SpaceGits } from "@indietabletop/types";
import { groupBy, uniq } from "es-toolkit";
import { database } from "../../database.ts";
import type { Gang } from "../../engine/Gang.ts";
import { Git } from "../../engine/Git.ts";
import { Vehicle } from "../../engine/Vehicle.ts";
import { FormDialog, FormDialogActions } from "../FormDialog/FormDialog.tsx";
import { FormDialogSubmitButton } from "../forms/DialogSubmitButton.tsx";
import { FormOptions } from "../forms/FormTextField.tsx";
import { GitTypeFormOption } from "../GitFormDialog/GitTypeFormOption.tsx";
import { VehicleFormOption } from "../VehicleFormDialog/VehicleFormOption.tsx";
import { CapsOverviewBanner } from "./CapsOverviewBanner.tsx";
import { Counter } from "./Counter.tsx";
import { gitsAndVehiclesDialog } from "./style.css.ts";
import { useCountsById } from "./useCountsById.ts";
import { useGangContext } from "./useGang.ts";

function formValuesToGangData({
  values,
  counts,
  gang,
}: {
  values: { gitTypeIds: string[]; vehicleTypeIds: string[] };
  counts: Map<string, number>;
  gang: Gang;
}): Partial<SpaceGits.GangData> {
  // Note that we have to use gang.gits/vehicles, to make sure that the order
  // of models is retained as these arrays are sorted differently than whatever
  // is in gang.data.
  const gitsByTypeId = groupBy(gang.gits, (g) => g.originalType.id);
  const vehiclesByTypeId = groupBy(gang.vehicles, (v) => v.originalType.id);

  return {
    gits: values.gitTypeIds.flatMap((typeId) => {
      return Array.from({ length: counts.get(typeId) ?? 1 }, (_, index) => {
        const existingGit = gitsByTypeId[typeId]?.[index]?.data;
        if (existingGit) {
          return { ...existingGit, weapons: [], gear: [], treatments: [] };
        }
        return Git.initData({ type: { id: typeId } });
      });
    }),

    vehicles: values.vehicleTypeIds.flatMap((typeId) => {
      return Array.from({ length: counts.get(typeId) ?? 1 }, (_, index) => {
        const existingVehicle = vehiclesByTypeId[typeId]?.[index]?.data;
        if (existingVehicle) {
          return { ...existingVehicle, weapons: [], repairs: [], mods: [] };
        }
        return Vehicle.initData({ type: { id: typeId } });
      });
    }),
  };
}

function countModels(
  typeId: string,
  models: (SpaceGits.GitData | SpaceGits.VehicleData)[],
) {
  return models.reduce((acc, ref) => acc + (ref.type.id === typeId ? 1 : 0), 0);
}

function typeToEntries(
  typeId: string,
  models: (SpaceGits.GitData | SpaceGits.VehicleData)[],
): [string, number] {
  const typeCount = countModels(typeId, models);
  const initialCount = Math.max(typeCount, 1);

  return [typeId, initialCount];
}

export function ChooseGitsAndVehiclesDialog() {
  const { gang } = useGangContext();
  const dialog = useDialogContext();
  const gitTypes = gang.ruleset.gits.toArray();
  const vehicleTypes = gang.ruleset.vehicles.toArray();

  const { counts, increment, decrement } = useCountsById(
    new Map([
      ...gitTypes.map((type) => typeToEntries(type.id, gang.data.gits)),
      ...vehicleTypes.map((type) => typeToEntries(type.id, gang.data.vehicles)),
    ]),
  );

  const { form, submitName } = useForm({
    defaultValues: {
      gitTypeIds: uniq(gang.gits.map((g) => g.originalType.id)),
      vehicleTypeIds: uniq(gang.vehicles.map((v) => v.originalType.id)),
    },

    onSubmit({ values, ...rest }) {
      console.log(rest);

      const modelData = formValuesToGangData({ values, counts, gang });
      const { data } = gang
        .clone({
          ...modelData,
          mode: { type: "CHEFS_REC", runCount: 0 },
        })
        .nameUnnamedGits();

      return database.gangs.updateOne(data);
    },

    onSuccess() {
      dialog?.hide();
    },
  });

  const values = useStoreState(form, (s) => s.values);
  const draftGang = gang.clone(formValuesToGangData({ values, counts, gang }));
  const baseCost = draftGang.baseCost;
  const maxCost = gang.ruleset.constants.chefsRecMaxModelsCost;
  const isOverMaxCost = baseCost > maxCost;

  return (
    <FormDialog heading="Choose Gits/Vehicles" form={form}>
      <CapsOverviewBanner
        cost={baseCost}
        label="Base cost"
        additionalInfo={`Max ${maxCost}`}
        isInvalid={isOverMaxCost}
      >
        {isOverMaxCost ? (
          <>
            <em className={gitsAndVehiclesDialog.emphasis}>Hold up!</em> You
            have crossed the {maxCost} caps limit for naked gits and stock
            vehicles!
          </>
        ) : (
          <>Pick up to {maxCost} caps worth of naked gits and stock vehicles.</>
        )}
      </CapsOverviewBanner>

      <FormOptions label="Gits">
        {gitTypes.map((profile) => {
          return (
            <GitTypeFormOption
              type="checkbox"
              profile={profile}
              key={profile.id}
              name={form.names.gitTypeIds}
              counter={
                values.gitTypeIds.includes(profile.id) && (
                  <Counter
                    onIncrement={() => void increment(profile.id)}
                    onDecrement={() => void decrement(profile.id)}
                    count={counts.get(profile.id) ?? 1}
                  />
                )
              }
            />
          );
        })}
      </FormOptions>
      <FormOptions label="Vehicles">
        {vehicleTypes.map((profile) => {
          return (
            <VehicleFormOption
              key={profile.id}
              profile={profile}
              name={form.names.vehicleTypeIds}
              type="checkbox"
              counter={
                values.vehicleTypeIds.includes(profile.id) && (
                  <Counter
                    onIncrement={() => void increment(profile.id)}
                    onDecrement={() => void decrement(profile.id)}
                    count={counts.get(profile.id) ?? 1}
                  />
                )
              }
            />
          );
        })}
      </FormOptions>

      <FormDialogActions>
        <LetterheadSubmitError name={submitName} />
        <FormDialogSubmitButton>Save</FormDialogSubmitButton>
      </FormDialogActions>
    </FormDialog>
  );
}
